"use client";

import React, { useEffect, useRef, useState } from "react";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import useBodyScrollLock from "@/hooks/useBodyScrollLock";

import { useTranslations } from "next-intl";

import BupiLinkButton from "@/components/atoms/linkButton";
import BupiLink from "@/components/atoms/link";
import AgoraButton from "@/components/atoms/agoraButton";
import { getMenuById, HeaderProps } from "@/page-builder/global/mappers/header.mapper";
import { useRouter } from "next/navigation";


const AreaSelector = ({
  topBar,
  menuItems,
  ctaButton,
}: HeaderProps) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeSecondLevel, setActiveSecondLevel] = useState<string | null>(null);
  const { isDesktop, isMobile } = useMediaQuery();
  const t = useTranslations('header');

  const menuRef = useRef<HTMLDivElement | null>(null);
  const menuButtonRef = useRef<HTMLButtonElement | null>(null);
  const menuRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const backButtonRef = useRef<HTMLButtonElement | null>(null);
  const submenuItemsRef = useRef<Array<HTMLAnchorElement | null>>([]);

  const activeMenu = getMenuById(menuItems, activeSecondLevel || '');
  const router = useRouter()

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
    setActiveSecondLevel(null);
  };

  const handleMenuItemClick = (menuId: string) => {
    setActiveSecondLevel(menuId);
  };

  const handleBackToMainMenu = () => {
    setActiveSecondLevel(null);
    setTimeout(() => {
      menuRefs.current[0]?.focus();
    }, 0);
  };

  useEffect(() => {
    const handleEscapeKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isMenuOpen) {
        setIsMenuOpen(false);
        menuButtonRef.current?.focus();
      }
    };

    document.addEventListener("keydown", handleEscapeKey);
    return () => document.removeEventListener("keydown", handleEscapeKey);
  }, [isMenuOpen]);

  useEffect(() => {
    if (!isMenuOpen || !menuRef.current) return;

    const getFocusableElements = () => {
      if (activeSecondLevel) {
        return [
          backButtonRef.current,
          ...submenuItemsRef.current.filter(Boolean),
          document.querySelector('.w-\\[90\\%\\]') as HTMLElement,
        ].filter(Boolean) as HTMLElement[];
      } else {
        return [
          ...menuRefs.current.filter(Boolean),
          document.querySelector('.w-\\[90\\%\\]') as HTMLElement,
        ].filter(Boolean) as HTMLElement[];
      }
    };

    const handleTabKey = (e: KeyboardEvent) => {
      if (e.key !== "Tab") return;
      
      const focusableElements = getFocusableElements();
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];
      
      if (e.shiftKey && document.activeElement === firstElement) {
        e.preventDefault();
        lastElement.focus();
      } else if (!e.shiftKey && document.activeElement === lastElement) {
        e.preventDefault();
        firstElement.focus();
      }
    };

    setTimeout(() => {
      if (!activeSecondLevel && menuRefs.current[0]) {
        menuRefs.current[0].focus();
      } else if (activeSecondLevel && backButtonRef.current) {
        backButtonRef.current.focus();
      }
    }, 10);

    document.addEventListener("keydown", handleTabKey);
    return () => document.removeEventListener("keydown", handleTabKey);
  }, [isMenuOpen, activeSecondLevel]);

  const handleKeyDown = (e: React.KeyboardEvent, index: number, isSubmenu: boolean = false) => {
    const refs = isSubmenu ? submenuItemsRef : menuRefs;
    const count = refs.current.filter(Boolean).length;
    
    if (e.key === "ArrowDown") {
      e.preventDefault();
      refs.current[(index + 1) % count]?.focus();
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      refs.current[(index - 1 + count) % count]?.focus();
    }
  };

  const handleMenuKeyDown = (e: React.KeyboardEvent, menuId: string, index: number) => {
    handleKeyDown(e, index);
    
    if (e.key === "Enter" || e.key === " " || e.key === "ArrowRight") {
      e.preventDefault();
      handleMenuItemClick(menuId);
    }
  };

  const handleBackKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " " || e.key === "ArrowLeft") {
      e.preventDefault();
      handleBackToMainMenu();
    }
  };

  const openLink = (item: any) => {
    router.push(item?.href ?? '/'); 
    toggleMenu()
  };

  useBodyScrollLock(isMenuOpen);

  const activeSubmenu = activeMenu?.submenu;
  
  return (
    <div>
      <div
        className="areas"
        aria-label={`${t('areaSelector.selectArea')}`}
        role={isDesktop ? "tablist" : "navigation"}
      >
        <div className="institutional-phrase-container sr-only">
          <div className="institutional"> {topBar?.platformLabel} </div>
        </div>
        
        {isDesktop &&
          <span
            role="tab"
            className={`area selected`}
          >
             {topBar?.platformLabel}
          </span>
        }

        {!isDesktop && (
          <AgoraButton
            ref={menuButtonRef}
            aria-controls="panel-content-MenuBurger"
            aria-expanded={isMenuOpen}
            aria-label={isMenuOpen ? `${t('areaSelector.openMenu')}` : `${t('areaSelector.closeMenu')}`}
            className={`flex items-center justify-center !no-underline agora-btn agora-btn-link-neutral panel-menu-button has-panel ${
              isMenuOpen ? "expanded" : "collapsed"
            } !px-0 !py-[14px]`}
            onClick={toggleMenu}
            leadingIcon="agora-line-menu"
            hasIcon
          >
            { !isMobile && <span>{`${t('areaSelector.menu')}`}</span> }
          </AgoraButton>
        )}

        {!isDesktop && isMenuOpen && (
          <div
            ref={menuRef}
            className="panel-menu-content !bg-[#F9F9FA] expanded"
            role="dialog"
            aria-modal="true"
            aria-label={`${t('areaSelector.navigationMenu')}`}
            id="panel-content-MenuBurger"
          >
            <div
              className="flex mb-32 justify-between flex-col overflow-y-auto"
              style={{ height: "calc(-80px + 100dvh)" }}
            >
              {!activeSecondLevel ? (
                <div className="navigation-burger-layout">
                  <div
                    id="navigation-burger-current-area"
                    className="current-area"
                    aria-live="polite"
                  >
                    BUPi
                  </div>
                  <nav className="xs:col-span-4 md:col-span-8 xl:col-span-12" aria-label={`${t('areaSelector.menu')}`}>
                    <ul>
                      {menuItems.map((item, index) => (
                        <li key={item.id} className="menu-item">
                          <AgoraButton
                            ref={(el) => { menuRefs.current[index] = el; }}
                            hasIcon={item.hasSubmenu}
                            onKeyDown={(e) => handleMenuKeyDown(e, item.id, index)}
                            trailingIcon={item.hasSubmenu ? "agora-line-chevron-right" : undefined}
                            aria-haspopup={item.hasSubmenu ? "true" : undefined}
                            aria-expanded={item.hasSubmenu ? activeSecondLevel === item.id : undefined}
                            aria-label={item.hasSubmenu ? `${item.title}, submenu` : item.title}
                            className="flex items-center justify-center bupi-menu agora-btn agora-btn-full agora-btn-link-neutral agora-btn-with-icon burger-menu-item-btn"
                            appearance="link"
                            onClick={() => item.hasSubmenu ? handleMenuItemClick(item.id) : openLink(item) }
                          >
                            {item.title}
                          </AgoraButton>
                        </li>
                      ))}
                    </ul>
                  </nav>
                </div>
              ) : (
                <div>
                  <div className="navigation-burger-layout">
                    <div className="container mx-auto">
                      <AgoraButton
                        hasIcon={true}
                        appearance="link"
                        leadingIcon="agora-solid-arrow-left-anchor"
                        className="px-0"
                        onClick={handleBackToMainMenu}
                        onKeyDown={handleBackKeyDown}
                        ref={backButtonRef}
                        aria-label={`${t('areaSelector.backToMenu')}`}
                      >
                        <span className="text-m-medium">{`${t('areaSelector.back')}`}</span>
                      </AgoraButton>
                    </div>
                  </div>

                  <div className="container mx-auto xs:pb-0 md:pb-32 xl:pb-32">
                    <h2 className="text-m-semibold text-neutral-500" id="submenu-title">
                      {activeSubmenu?.title}
                    </h2>
                    <h3 className="py-32 text-neutral-900 text-xl-bold"> {activeSubmenu?.subtitle} </h3>
                    <nav aria-label={`${activeSubmenu?.title || t('areaSelector.menu')} submenu`}>
                      <ul className="grid xs:grid-cols-4 md:grid-cols-8 xl:grid-cols-12 gap-y-16 gap-x-32">
                        {activeSubmenu?.links.map((link, index) => (
                          <li
                            key={link.href}
                            className="link-wrapper xs:col-span-4 md:col-span-4 xl:col-span-6"
                          >
                            <span className="">
                              <BupiLink
                                key={link.text}
                                label={link.text}
                                url={link.href}
                                target="_self"
                                className='py-8'
                                hasIcon
                                 onClick={() => toggleMenu()}
                                ref={(el) => { submenuItemsRef.current[index] = el; }}
                                onKeyDown={(e) => handleKeyDown(e, index, true)}
                              />
                            </span>
                          </li>
                        ))}
                      </ul>
                    </nav>
                  </div>
                </div>
              )}
              
              <div className="mb-6 flex justify-center">
              <BupiLinkButton
                  hasIcon={true}
                  className="w-[90%]"
                  leadingIcon="agora-line-arrow-right-circle"
                  link={ctaButton?.url || 'https://cidadao.bupi.gov.pt/'}
                  label={ctaButton?.label || 'Plataforma'}
                  target={ctaButton?.target || "_blank"}
                />
               
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AreaSelector;
